import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ASSETS, LINKS } from "../lib/assets";
import { Reveal, MaskedLine, GoldLine, Eyebrow } from "../components/Reveal";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const crestY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ivory pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-44" data-testid="home-hero">
      <span className="pointer-events-none absolute -right-10 top-24 select-none font-heading text-[10rem] font-semibold leading-none text-navy/[0.04] sm:text-[16rem]" aria-hidden="true">
        1989
      </span>
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="min-w-0 lg:col-span-7">
          <MaskedLine delay={0.1}>
            <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-semibold leading-[0.95] text-navy">
            {t.common.mottoWords.map((w, i) => (
              <MaskedLine key={w} delay={0.25 + i * 0.14} className="text-5xl sm:text-7xl lg:text-[6.5rem]">
                <span className="inline-flex items-baseline">
                  {w}
                  {i < 2 && <span className="ml-4 inline-block h-2.5 w-2.5 rounded-full bg-gold sm:ml-6 sm:h-3.5 sm:w-3.5" aria-hidden="true" />}
                </span>
              </MaskedLine>
            ))}
          </h1>
          <GoldLine className="mt-10 w-24" delay={0.8} />
          <MaskedLine delay={0.85}>
            <p className="mt-8 max-w-xl font-heading text-2xl font-medium leading-snug text-navy sm:text-3xl">
              {t.common.schoolName}
            </p>
          </MaskedLine>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 max-w-lg text-base font-light leading-relaxed text-ink/70 md:text-lg"
          >
            {t.hero.since} {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/about"
              data-testid="hero-discover-btn"
              className="group inline-flex items-center gap-3 bg-navy px-7 py-3.5 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-navy-soft"
            >
              {t.common.discover}
              <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 border border-navy/20 px-7 py-3.5 text-sm font-medium text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {t.common.contactUs}
            </Link>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <span className="absolute -left-4 -top-4 h-full w-full border border-gold/60" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden">
              <img
                src={ASSETS.courtyard}
                alt="SMHSS campus courtyard"
                className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[540px]"
              />
              <span className="absolute inset-0 bg-navy/10" aria-hidden="true" />
            </motion.div>
            <motion.img
              style={{ y: crestY }}
              src={ASSETS.logo}
              alt="SMHSS school crest"
              className="absolute -bottom-10 -left-6 h-28 w-auto drop-shadow-lg sm:h-36 lg:-left-12"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const MottoMarquee = () => {
  const { t } = useLang();
  return (
    <div className="border-y border-gold/30 bg-cream py-5" data-testid="home-marquee">
      <Marquee speed={28} gradient={false} autoFill>
        {t.marquee.map((w, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-heading text-2xl font-medium text-navy/80">
            {w}
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" />
          </span>
        ))}
      </Marquee>
    </div>
  );
};

const FactStrip = () => {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  return (
    <section className="bg-navy py-14" data-testid="home-facts">
      <div
        className={`mx-auto grid max-w-7xl gap-x-6 gap-y-10 px-5 sm:px-8 ${
          ta ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
        }`}
      >
        {t.facts.map((f, i) => (
          <Reveal key={i} delay={i * 0.08} y={16} className={`flex min-w-0 flex-col items-start px-2 ${ta ? "" : "lg:border-l lg:border-gold/25 lg:px-6 lg:first:border-l-0"}`}>
            <span className="break-words text-[10px] uppercase tracking-[0.25em] text-gold">{f.label}</span>
            <span className={`mt-2 break-words font-heading font-semibold text-ivory ${ta ? "text-base sm:text-xl lg:text-2xl" : "text-xl sm:text-2xl"}`}>{f.value}</span>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Chapter = ({ num, eyebrow, children, className = "" }) => (
  <div className={`mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 lg:grid-cols-12 ${className}`}>
    <Reveal className="min-w-0 lg:col-span-2">
      <span className="font-heading text-6xl font-medium text-gold/50">{num}</span>
      <Eyebrow className="mt-3 block">{eyebrow}</Eyebrow>
    </Reveal>
    <div className="min-w-0 lg:col-span-10">{children}</div>
  </div>
);

const Intro = () => {
  const { t } = useLang();
  return (
    <section className="bg-ivory py-24 lg:py-32" data-testid="home-intro">
      <Chapter num="01" eyebrow={t.home.introEyebrow}>
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-6">
            <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{t.home.introTitle}</h2>
            <GoldLine className="mt-8 w-16" />
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ink/75 md:text-lg">{t.home.introBody}</p>
          </Reveal>
          <Reveal delay={0.15} className="min-w-0 pr-3 pt-3 lg:col-span-6">
            <div className="relative">
              <span className="absolute -right-3 -top-3 h-full w-full border border-gold/50" aria-hidden="true" />
              <div className="relative overflow-hidden">
                <motion.img
                  src={ASSETS.building}
                  alt="SMHSS school building"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="h-72 w-full object-cover sm:h-96"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </Chapter>
    </section>
  );
};

const Philosophy = () => {
  const { t } = useLang();
  return (
    <section className="bg-warmneutral py-24 lg:py-32" data-testid="home-philosophy">
      <Chapter num="02" eyebrow={t.home.philosophyEyebrow}>
        <Reveal>
          <h2 className="max-w-2xl font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{t.home.philosophyTitle}</h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-ink/70 md:text-lg">{t.home.philosophyIntro}</p>
        </Reveal>
        <div className="mt-14 grid gap-px bg-gold/30 sm:grid-cols-3">
          {t.philosophy.map((p, i) => (
            <Reveal key={i} delay={i * 0.12} className="group bg-warmneutral p-8 transition-colors duration-500 hover:bg-ivory lg:p-10">
              <span className="text-xs tracking-[0.3em] text-gold">{p.num}</span>
              <h3 className="mt-4 font-heading text-3xl font-semibold text-navy">{p.word}</h3>
              <span className="mt-4 block h-px w-8 bg-gold transition-[width] duration-500 group-hover:w-16" aria-hidden="true" />
              <p className="mt-5 text-sm font-light leading-relaxed text-ink/70">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </Chapter>
    </section>
  );
};

const AcademicsPreview = () => {
  const { t } = useLang();
  return (
    <section className="bg-ivory py-24 lg:py-32" data-testid="home-academics">
      <Chapter num="03" eyebrow={t.home.academicsEyebrow}>
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-5">
            <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{t.home.academicsTitle}</h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-ink/70 md:text-lg">{t.home.academicsBody}</p>
            <Link
              to="/academics"
              data-testid="home-academics-cta"
              className="group mt-10 inline-flex items-center gap-3 border border-navy px-7 py-3.5 text-sm font-medium text-navy transition-colors duration-300 hover:bg-navy hover:text-ivory"
            >
              {t.home.academicsCta}
              <ArrowRight size={16} className="text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="min-w-0 lg:col-span-7">
            {t.journey.map((j, i) => (
              <Reveal key={i} delay={i * 0.08} y={18}>
                <div className="group flex items-baseline justify-between gap-4 border-b border-navy/10 py-5 transition-colors duration-300 hover:border-gold">
                  <span className="min-w-0 break-words font-heading text-2xl font-medium text-navy md:text-3xl">{j.stage}</span>
                  <span className={`min-w-0 break-words text-right text-xs uppercase tracking-[0.2em] md:text-sm ${j.soon ? "text-gold" : "text-ink/50"}`}>
                    {j.level}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Chapter>
    </section>
  );
};

const Legacy = () => {
  const { t } = useLang();
  return (
    <section className="bg-navy-deep py-24 text-ivory lg:py-32" data-testid="home-legacy">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <Reveal className="min-w-0 lg:col-span-2">
          <span className="font-heading text-6xl font-medium text-gold/60">04</span>
          <Eyebrow light className="mt-3">{t.home.legacyEyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.1} className="min-w-0 lg:col-span-5">
          <h2 className="font-heading text-4xl font-semibold leading-tight md:text-5xl">{t.home.legacyTitle}</h2>
          <GoldLine className="mt-8 w-16" />
          <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-ivory/75 md:text-lg">{t.home.legacyBody}</p>
          <Link
            to="/about"
            data-testid="home-legacy-cta"
            className="group mt-10 inline-flex items-center gap-3 border border-gold/60 px-7 py-3.5 text-sm font-medium text-gold transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            {t.home.legacyCta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.2} className="min-w-0 lg:col-span-5">
          <div className="relative overflow-hidden">
            <motion.img
              src={ASSETS.corridor}
              alt="SMHSS entrance corridor with founding plaques"
              whileHover={{ scale: 1.04 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="h-80 w-full object-cover sm:h-[420px]"
            />
            <span className="absolute inset-0 border border-gold/30" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Campus = () => {
  const { t } = useLang();
  const imgs = { building: ASSETS.building, courtyard: ASSETS.courtyard, classroom: ASSETS.classroom, library: ASSETS.library, corridor: ASSETS.corridor };
  const spans = ["sm:col-span-3 sm:row-span-2", "sm:col-span-3", "sm:col-span-2", "sm:col-span-2", "sm:col-span-2"];
  return (
    <section className="bg-ivory py-24 lg:py-32" data-testid="home-campus">
      <Chapter num="05" eyebrow={t.home.campusEyebrow}>
        <Reveal>
          <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{t.home.campusTitle}</h2>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-ink/70 md:text-lg">{t.home.campusBody}</p>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-6">
          {t.gallery.map((g, i) => (
            <Reveal key={g.key} delay={i * 0.08} className={`group relative overflow-hidden ${spans[i]}`}>
              <motion.img
                src={imgs[g.key]}
                alt={g.caption}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`w-full object-cover ${i === 0 ? "h-72 sm:h-full" : "h-56 sm:h-64"}`}
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/70 to-transparent px-5 pb-4 pt-14 text-sm text-ivory opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                {g.caption}
              </span>
            </Reveal>
          ))}
        </div>
      </Chapter>
    </section>
  );
};

const Visit = () => {
  const { t } = useLang();
  return (
    <section className="border-t border-gold/25 bg-cream py-24 lg:py-28" data-testid="home-visit">
      <Chapter num="06" eyebrow={t.home.visitEyebrow}>
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-7">
            <h2 className="font-heading text-4xl font-semibold leading-tight text-navy md:text-5xl">{t.home.visitTitle}</h2>
            <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-ink/70 md:text-lg">{t.home.visitBody}</p>
            <p className="mt-6 flex items-start gap-3 text-sm font-light text-ink/70">
              <MapPin size={16} className="mt-0.5 shrink-0 text-gold" />
              <span>
                {t.common.addressLines[0]} {t.common.addressLines[1]}
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.15} className="flex flex-wrap gap-4 lg:col-span-5 lg:justify-end">
            <a
              href={LINKS.tel}
              data-testid="home-visit-call"
              className="inline-flex items-center gap-3 bg-navy px-7 py-3.5 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-navy-soft"
            >
              <Phone size={15} className="text-gold" />
              {t.common.callSchool}
            </a>
            <Link
              to="/contact"
              data-testid="home-visit-contact"
              className="inline-flex items-center gap-3 border border-navy/25 px-7 py-3.5 text-sm font-medium text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {t.common.contactUs}
            </Link>
          </Reveal>
        </div>
      </Chapter>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <MottoMarquee />
      <FactStrip />
      <Intro />
      <Philosophy />
      <AcademicsPreview />
      <Legacy />
      <Campus />
      <Visit />
    </>
  );
}
